import React, { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { fetchDecodedPeaks, getCachedDecoded } from '../utils/peaksCache';

// Downsample raw PCM samples into RMS-aggregated peaks. Mixes are loudness-
// maxed so max-amplitude peaks come out near-uniform; RMS reflects perceived
// energy and gives the "flatter with subtle dynamics" shape of real mix
// waveforms (like SoundCloud/Mixcloud). 1024 buckets ≈ one per bar at typical
// card width, which keeps memory + render time low.
function computeRmsPeaks(samples, buckets = 1024) {
    if (!samples || !samples.length) return null;
    const bucketSize = Math.max(1, Math.floor(samples.length / buckets));
    const out = new Float32Array(buckets);
    for (let b = 0; b < buckets; b++) {
        const start = b * bucketSize;
        const end = Math.min(start + bucketSize, samples.length);
        let sum = 0;
        for (let i = start; i < end; i++) sum += samples[i] * samples[i];
        out[b] = Math.sqrt(sum / (end - start));
    }
    return out;
}

// Mix waveform is lazy: we only kick off the yt-dlp peaks download when this
// mix becomes the active player track. Full-length mixes take 20–30s to
// download even at low bitrate; eagerly fetching for every visible card
// saturated the system and made the feed feel slow. Until active, render
// nothing (the FakeMixWaveform underneath stays visible).
const MixFeedWaveform = ({ mixLink, mixId, onPlay }) => {
    const containerRef = useRef(null);
    const wsRef = useRef(null);
    const [ready, setReady] = useState(false);
    // The URL the PLAYER is actually streaming for THIS mix when it differs
    // from our mixLink prop — the dead-link case: LAZY_MIX_REPLACE swaps in
    // a working link at play time, peaks get cached under the NEW url, and
    // url-matching alone would leave this card blank forever. The player
    // broadcasts its mixId exactly so the card can recognize its mix and
    // adopt the live URL.
    const [adoptedUrl, setAdoptedUrl] = useState(() => (
        (mixId != null
            && window.__currentPlayerMixId === String(mixId)
            && window.__currentPlayerOriginalUrl)
        || null
    ));
    const effectiveLink = adoptedUrl || mixLink;
    // Peaks fetch failed (or is taking absurdly long): drop the loader so the
    // FakeMixWaveform underneath shows again — FeedTab.scss hides the fake
    // whenever a .feed-waveform-loader exists, so a loader that never clears
    // used to suppress BOTH waveforms forever. The container stays mounted,
    // so peaks that land late still paint and flip `ready` as usual.
    const [failed, setFailed] = useState(false);
    const [shouldLoad, setShouldLoad] = useState(false);
    const rafRef = useRef(null);
    const activeRef = useRef(false);
    const readyRef = useRef(false);
    readyRef.current = ready;
    // True when the paint came from already-decoded peaks (remount after
    // back-nav while the mix keeps playing): skip the opacity fade so the
    // waveform is just *there*. Fresh downloads keep the fade.
    const instantRef = useRef(false);

    const getColors = useCallback(() => {
        const canvas = document.createElement('canvas');
        canvas.height = 60;
        const ctx = canvas.getContext('2d');
        const wave = ctx.createLinearGradient(0, 0, 0, 60);
        // Shared palette for BOTH themes: a mid-grey gradient for the unplayed
        // bars — visible against both the dark and the light card — plus a clear
        // cyan for the played portion. Keeps the waveform identical in either
        // theme (the dark-theme played already read as ~#00bcd4).
        wave.addColorStop(0, '#a6abb0');
        wave.addColorStop(1, '#8d9297');
        // DESIGN V2: same palette as FeedWaveform — unplayed bars take the
        // --v2-wave token (ink at a third, per theme), played takes the accent.
        if (document.documentElement.dataset.design === 'v2') {
            const css = getComputedStyle(document.documentElement);
            const accent = css.getPropertyValue('--color-accent').trim();
            const unplayed = css.getPropertyValue('--v2-wave').trim();
            const top = css.getPropertyValue('--v2-wave-played-top').trim();
            const bottom = css.getPropertyValue('--v2-wave-played-bottom').trim();
            if (accent) {
                let played = accent;
                if (top && bottom) {
                    played = ctx.createLinearGradient(0, 0, 0, 80);
                    played.addColorStop(0, top); played.addColorStop(1, bottom);
                }
                return { waveColor: unplayed || wave, progressColor: played };
            }
        }
        return { waveColor: wave, progressColor: '#00bcd4' };
    }, []);

    useEffect(() => {
        if (!shouldLoad || !effectiveLink || !containerRef.current) return;
        let cancelled = false;

        const createWs = (peaks, duration) => {
            if (cancelled || !containerRef.current) return;
            const { waveColor, progressColor } = getColors();
            try {
                const ws = WaveSurfer.create({
                    container: containerRef.current,
                    waveColor, progressColor,
                    cursorWidth: 0, barWidth: 2, barGap: 2, barRadius: 1,
                    // Design v2: 80px so the stack (title block + wave) fills the 132px art; classic 60.
                        height: document.documentElement.dataset.design === 'v2' ? 80 : 60, normalize: true, interact: false,
                    peaks: peaks ? [peaks] : undefined,
                    duration: duration || undefined,
                    hideScrollbar: true,
                });
                // Seed the live progress before the first visible paint (same
                // as FeedWaveform): the RAF tick only seeks after activation,
                // which used to leave the first painted frames all-grey on a
                // remount mid-playback.
                const seekToLive = () => {
                    const audio = window.musicPlayerInstance;
                    const isActive = (window.__currentPlayerOriginalUrl && window.__currentPlayerOriginalUrl === effectiveLink)
                        || (mixId != null && window.__currentPlayerMixId === String(mixId));
                    if (!isActive || !audio || !(audio.duration > 0)) return;
                    try { ws.seekTo(Math.min(1, audio.currentTime / audio.duration)); } catch { /* not fatal */ }
                };
                seekToLive();
                ws.on('ready', () => { if (!cancelled) { seekToLive(); setReady(true); } });
                ws.on('error', () => { if (!cancelled) setFailed(true); });
                wsRef.current = ws;
            } catch {
                // WaveSurfer failed — clear the loader so FakeMixWaveform
                // (stays rendered underneath) is actually visible again.
                if (!cancelled) setFailed(true);
            }
        };

        // Backstop: a mix download can legitimately take a couple of minutes,
        // but past that the loader is just hiding the fake waveform. Late
        // peaks still paint (see `failed` above), so this only bounds the
        // spinner, not the fetch.
        const failTimer = setTimeout(() => {
            if (!cancelled && !readyRef.current) setFailed(true);
        }, 180000);

        // Cache hit — paint synchronously with no fade (remount after
        // back-nav). Only misses go through the async download below.
        const cached = getCachedDecoded(effectiveLink);
        if (cached) {
            instantRef.current = true;
            createWs(computeRmsPeaks(cached.peaks, 1024) || cached.peaks, cached.duration);
        } else {
            instantRef.current = false;
            (async () => {
                const entry = await fetchDecodedPeaks(effectiveLink);
                if (cancelled || !containerRef.current) return;
                if (entry) {
                    const rms = computeRmsPeaks(entry.peaks, 1024) || entry.peaks;
                    createWs(rms, entry.duration);
                } else {
                    // Download/decode failed — fetchDecodedPeaks resolved null.
                    setFailed(true);
                }
            })();
        }

        return () => {
            cancelled = true;
            clearTimeout(failTimer);
            if (wsRef.current) {
                try { wsRef.current.unAll(); wsRef.current.destroy(); } catch {}
                wsRef.current = null;
            }
            setReady(false);
            setFailed(false);
        };
    }, [shouldLoad, effectiveLink]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const stopRaf = () => {
            if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        };

        // Keep the RAF loop alive while this mix is active, even before the
        // wavesurfer is ready. player-track-change fires the instant the mix
        // starts, but fetchDecodedPeaks takes ~20s. If we stop the loop on
        // null wsRef, it never picks up the player's position once the
        // waveform finally loads — so seek/scrub in the player doesn't
        // reflect on the card. Just skip the seekTo call until wsRef exists.
        const tick = () => {
            if (!activeRef.current) { stopRaf(); return; }
            const audio = window.musicPlayerInstance;
            if (audio && wsRef.current) {
                const dur = audio.duration;
                if (dur > 0) {
                    try { wsRef.current.seekTo(Math.min(1, audio.currentTime / dur)); } catch {}
                }
            }
            rafRef.current = requestAnimationFrame(tick);
        };

        const handler = (e) => {
            const { originalUrl: activeOriginalUrl, mixId: activeMixId } = e.detail || {};
            const matchesById = mixId != null && activeMixId != null && activeMixId === String(mixId);
            const matches = (effectiveLink && activeOriginalUrl === effectiveLink) || matchesById;
            if (matches) {
                // Matched by id but the player streams a different link —
                // dead link replaced at play time. Adopt it as the peaks
                // source (its peaks are what the bottom player decoded).
                if (matchesById && activeOriginalUrl && activeOriginalUrl !== effectiveLink) {
                    setAdoptedUrl(activeOriginalUrl);
                }
                activeRef.current = true;
                setShouldLoad(true);
                stopRaf();
                rafRef.current = requestAnimationFrame(tick);
            } else if (activeRef.current) {
                activeRef.current = false;
                stopRaf();
                if (readyRef.current) {
                    try { if (wsRef.current) wsRef.current.seekTo(0); } catch {}
                } else {
                    // User switched away before load finished — hide the loader
                    // so it doesn't linger forever. Next activation restarts.
                    setShouldLoad(false);
                }
            }
        };

        window.addEventListener('player-track-change', handler);
        // Catch-up: if this mix is already playing when we mount (e.g. the
        // card was scrolled off-screen and back), go active immediately.
        const activeUrl = window.__currentPlayerOriginalUrl;
        const activeMixId = window.__currentPlayerMixId || null;
        if ((activeUrl && activeUrl === effectiveLink)
            || (mixId != null && activeMixId === String(mixId))) {
            activeRef.current = true;
            setShouldLoad(true);
            stopRaf();
            rafRef.current = requestAnimationFrame(tick);
        }
        return () => {
            window.removeEventListener('player-track-change', handler);
            stopRaf();
        };
    }, [effectiveLink, mixId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClick = useCallback((e) => {
        if (!ready || !containerRef.current) return;
        e.stopPropagation();
        if (activeRef.current && window.musicPlayerInstance) {
            const rect = containerRef.current.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const dur = window.musicPlayerInstance.duration;
            if (dur > 0) {
                window.musicPlayerInstance.currentTime = ratio * dur;
                try { if (wsRef.current) wsRef.current.seekTo(ratio); } catch {}
            }
        } else if (onPlay) {
            onPlay();
        }
    }, [ready, onPlay]);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (wsRef.current) {
                const { waveColor, progressColor } = getColors();
                wsRef.current.setOptions({ waveColor, progressColor });
            }
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, [getColors]);

    if (!effectiveLink || !shouldLoad) return null;

    return (
        <>
            <div
                className={`feed-waveform-real${ready ? ' is-ready' : ''}`}
                ref={containerRef}
                onClick={handleClick}
                style={{ opacity: ready ? 1 : 0, transition: instantRef.current ? 'none' : 'opacity 0.3s', cursor: 'pointer' }}
            />
            {!ready && !failed && (
                <div className="feed-waveform-loader" aria-label="Loading mix waveform">
                    <div className="feed-waveform-loader-bars" />
                </div>
            )}
        </>
    );
};

export default React.memo(MixFeedWaveform);
